import { Authenticated } from "components/Authenticated";
import { Spinner } from "components/Spinner";
import Notifications from "layout/Notifications";
import {
  ComponentType,
  ExoticComponent,
  lazy,
  LazyExoticComponent,
  Suspense
} from "react";
import { Route, Routes } from "react-router-dom";
import { routes } from "routes";

import { useAppInsightsPageViewTracker } from "./analytics/app-insights/app-insights.hooks";
import { usePageTitle } from "./core/hooks/usePageTitle";

interface BhwModuleRoute {
  path: string;
  allowAnonymous?: boolean;
  component: LazyExoticComponent<ComponentType>;
}

const Layout = lazy(() => import("layout/Layout"));

// Lazy components must be created once, at module scope. Creating them during
// render gives a new component type on every navigation, which remounts the
// whole module and flashes the full-screen loading spinner.
const modules: BhwModuleRoute[] = [
  {
    path: routes.bha.path,
    component: lazy(() => import("modules/bha"))
  },
  {
    path: routes.setup.path,
    allowAnonymous: true,
    component: lazy(() => import("modules/setup"))
  },
  {
    path: routes.booking.path,
    allowAnonymous: true,
    component: lazy(() => import("modules/bhb"))
  }
];

interface BhwModuleProps {
  allowAnonymous?: boolean;
  component: ExoticComponent;
}

const AppModule = ({
  allowAnonymous,
  component: Component
}: BhwModuleProps) => {
  if (allowAnonymous) {
    return <Component />;
  }

  return (
    <Authenticated
      renderSpinner={spinnerMessage => (
        <Spinner variant="fullscreen">{spinnerMessage}</Spinner>
      )}
    >
      <Component />
    </Authenticated>
  );
};

export const AppRoutes = () => {
  usePageTitle();
  useAppInsightsPageViewTracker();
  return (
    <>
      <Suspense
        fallback={
          <Spinner variant="fullscreen">Loading, please wait...</Spinner>
        }
      >
        <Routes>
          {modules.map(({ path, allowAnonymous, component }) => (
            <Route
              key={path}
              path={path}
              element={
                <AppModule
                  allowAnonymous={allowAnonymous}
                  component={component}
                />
              }
            />
          ))}

          <Route path="*" element={<Layout />} />
        </Routes>
      </Suspense>

      <Notifications />
    </>
  );
};
